/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useCampaignManager } from '../../communication/CampaignContext';
import { ProgrammableSurfacesCommunication } from '../../communication/ProgrammableSurfacesCommunication';
import { ProgrammableSurfacesEvents } from '../../communication/types';
import { strings } from '../../intl/Strings';
import { logger } from '../../shared';
import {
    CampaignConfig,
    CampaignStateResponsePayload,
    isCampaignStatePayload
} from '../../types/CampaignTypes';
import { ExcelBrainstormActionCard } from './components/actions/ExcelBrainstormActionCard';
import { ExcelLearnMoreActionCard } from './components/actions/ExcelLearnMoreActionCard';
import { ExcelTryCopilotActionCard } from './components/actions/ExcelTryCopilotActionCard';
import ConversionTrialsListSidePane from './components/conversionTrialsActionListSidePane';
import { TrialActionId, useTrialConversionStyles } from './components/shared';
import { OpenDialog } from '../../components/OpenDialog';
import { EntryPoint } from '../../../src/entryPointConfig';

/**
 * Campaign configuration for Excel trial conversion.
 * Tracks completion of Learn More, Try Copilot, and Brainstorm actions.
 */
const EXCEL_CAMPAIGN_CONFIG: CampaignConfig<TrialActionId> = {
    componentId: 'trial-conversion-excel',
    requiredActions: [
        TrialActionId.LearnMore,
        TrialActionId.TryCopilotDialog,
        TrialActionId.BrainStormDialog
    ],
    actionMapper: (schemaActionId: string): TrialActionId | null => {
        const normalized = schemaActionId.toLowerCase().trim();
        switch (normalized) {
            case 'learnmore':
                return TrialActionId.LearnMore;
            case 'trycopilotdialog':
                return TrialActionId.TryCopilotDialog;
            case 'brainstormdialog':
            case 'brainstormingdialog':
                return TrialActionId.BrainStormDialog;
            default:
                console.warn(`[ExcelExperiment] Unknown action ID ignored: ${schemaActionId}`);
                return null;
        }
    }
};

export const ExcelExperiment = () => {
    const styles = useTrialConversionStyles();

    // Campaign state management
    const [campaignState, setCampaignState] = useState<Set<TrialActionId>>(new Set());
    const [campaignStateReceived, setCampaignStateReceived] = useState(false);

    // Get campaign manager from React Context
    const campaignManager = useCampaignManager(EXCEL_CAMPAIGN_CONFIG);

    /**
     * Handles action click - updates campaign state via communication bridge.
     */
    const handleTaskClick = (actionId: TrialActionId, logTag: string, logMessage: string) => {
        logger.logInfo(logTag, logMessage);

        // Update campaign state if action not yet completed
        if (!campaignState.has(actionId)) {
            const newState = new Set(campaignState);
            newState.add(actionId);
            setCampaignState(newState);
            // Notify campaign manager to sync with Win32 via communication bridge
            campaignManager.completeAction(actionId);
        }
    };

    const openTrialBenefitsDialog = (entryPoint: EntryPoint, width: number, height: number) => {
        try {
            OpenDialog({
                entryPoint: entryPoint,
                width: width,
                height: height
            });
            logger.logInfo('OpenTrialBenefitsDialog', 'Trial benefits dialog opened successfully');
        } catch (error) {
            logger.logInfo('OpenTrialBenefitsDialog', `Failed to open trial benefits dialog: ${error}`);
        }
    };

    /**
     * Initialize campaign state from Win32 via communication bridge.
     */
    useEffect(() => {
        const communication = ProgrammableSurfacesCommunication.getInstance();

        const handleCampaignStateResponse = (payload: CampaignStateResponsePayload) => {
            try {
                if (!isCampaignStatePayload(payload)) {
                    console.error('[ExcelExperiment] Invalid CampaignStatePayload received:', payload);
                    return;
                }

                logger.logInfo('CampaignStateResponse', 'Campaign state response received from Win32');

                campaignManager.initializeCampaignState(payload);
                const completedActions = campaignManager.getCompletedActions();

                setCampaignState(completedActions);
                setCampaignStateReceived(true);

                logger.logInfo(
                    'CampaignStateInitialized',
                    `Excel trial conversion initialized with ${completedActions.size} completed actions from Win32`
                );
            } catch (error) {
                console.error('[ExcelExperiment] Error handling campaign state response:', error);
            }
        };

        communication.addEventListener<CampaignStateResponsePayload>(
            ProgrammableSurfacesEvents.CampaignStateResponse,
            handleCampaignStateResponse
        );

        logger.logInfo('RequestCampaignState', 'Requesting campaign state from Win32');
        campaignManager.requestCampaignState();

        return () => {
            communication.removeEventListener<CampaignStateResponsePayload>(
                ProgrammableSurfacesEvents.CampaignStateResponse,
                handleCampaignStateResponse
            );
        };
    }, [campaignManager]);

    const titles = [
        strings.getString('welcomeToM365'),
        strings.getString('trialconversionExcelTitle')
    ];

    // Recreate actions when campaignState updates to refresh checked status
    const actions = React.useMemo(() => {
        const learnMoreAction = ExcelLearnMoreActionCard();
        learnMoreAction.checked = campaignState.has(TrialActionId.LearnMore);
        learnMoreAction.onClick = () => {
            handleTaskClick(
                TrialActionId.LearnMore,
                'TrialConversionExcelCard',
                'Learn More clicked'
            );
            openTrialBenefitsDialog('trialBenefits', 850, 510);
        };

        const tryCopilotAction = ExcelTryCopilotActionCard();
        tryCopilotAction.checked = campaignState.has(TrialActionId.TryCopilotDialog);
        tryCopilotAction.onClick = () => handleTaskClick(
            TrialActionId.TryCopilotDialog,
            'TrialConversionExcelCard',
            'Try Copilot clicked'
        );

        const brainstormAction = ExcelBrainstormActionCard();
        brainstormAction.checked = campaignState.has(TrialActionId.BrainStormDialog);
        brainstormAction.onClick = () => handleTaskClick(
            TrialActionId.BrainStormDialog,
            'TrialConversionExcelCard',
            'Brainstorm clicked'
        );

        return [learnMoreAction, tryCopilotAction, brainstormAction];
    }, [campaignState]);

    return (
        <div className={styles.container}>
            <ConversionTrialsListSidePane actions={actions} titleLines={titles} />
        </div>
    );
};
